import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { UserCog, Lock, LogOut, Eye, ArrowLeft, AlertTriangle } from 'lucide-react';
import { BOLLETTO_LOGO_URL, LOGO_URL } from '../../utils/constants';
import { ChangePasswordModal } from './ChangePasswordModal';

const ROLE_LABELS = { admin: 'Amministratore', employee: 'Sales Executive', agent: 'Agenzia' };

const getInitials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

/** Pill di navigazione dell'header: sfondo vetro, bordo sottile, hover a contrasto pieno. */
const HeaderAction = ({ as: As = 'button', icon: Icon, label, testId, tone = 'default', ...rest }) => {
  const tones = {
    default: 'bg-white/10 hover:bg-white/25 border-white/20 hover:border-white/40 text-white/90 hover:text-white focus-visible:ring-white/60',
    danger: 'bg-red-500/20 hover:bg-red-500/40 border-red-300/40 hover:border-red-200/70 text-red-50 hover:text-white focus-visible:ring-red-300/70',
  };
  return (
    <As
      className={`group flex items-center gap-2 rounded-full border text-sm font-medium py-2 px-3.5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 ${tones[tone]}`}
      data-testid={testId}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={2.2} />
      <span className="hidden md:inline whitespace-nowrap">{label}</span>
    </As>
  );
};

/**
 * Header condiviso dell'app (Dashboard, Admin, Supervisione) — grafica unica per continuità.
 * Self-contained: gestisce internamente il cambio password e la conferma di uscita.
 * @param {Object} props
 * @param {{icon: React.ComponentType, label: string, hint?: string}} [props.section]
 *   Contesto della sezione corrente (mostrato a sinistra nelle pagine diverse dalla dashboard).
 */
export const DashboardHeader = ({ section = null }) => {
  const { user, logout, isAdmin } = useAuth();
  const roleLabel = ROLE_LABELS[user?.role] || 'Utente';
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#123012] via-[#1F4A1F] to-[#2E6B2E] border-b border-white/10 shadow-[0_4px_24px_-6px_rgba(0,0,0,0.5)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          {/* Brand + eventuale sezione */}
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Link to="/" className="flex items-center gap-4 shrink-0" data-testid="header-home-link">
              <img src={BOLLETTO_LOGO_URL} alt="Bolletto" className="h-8 w-auto brightness-0 invert" />
              <div className="hidden lg:flex items-center gap-2 pl-4 border-l border-white/15">
                <span className="text-white/40 text-xs">by</span>
                <img src={LOGO_URL} alt="Energy.Lab" className="h-5 w-auto brightness-0 invert opacity-70" />
              </div>
            </Link>

            {section && (
              <div className="flex items-center gap-2 pl-2 sm:pl-3 border-l border-white/15 min-w-0" data-testid="header-section">
                <Link to="/" className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors" title="Torna alla Dashboard" data-testid="header-back">
                  <ArrowLeft className="w-4 h-4" />
                </Link>
                <section.icon className="w-4 h-4 text-white/80 shrink-0" />
                <div className="flex flex-col leading-tight min-w-0">
                  <span className="text-white text-sm font-semibold truncate">{section.label}</span>
                  {section.hint && <span className="text-white/50 text-[11px] truncate hidden sm:block">{section.hint}</span>}
                </div>
              </div>
            )}
          </div>

          {/* Azioni */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            {/* Identità account — volutamente NON a forma di pulsante */}
            <div className="hidden sm:flex items-center gap-2.5 pr-1 mr-1 sm:pr-3 sm:mr-1 sm:border-r border-white/15 cursor-default select-none" data-testid="account-badge">
              <div className="w-9 h-9 rounded-full bg-white text-[#1F4A1F] flex items-center justify-center text-xs font-bold shadow-md ring-2 ring-white/20">
                {getInitials(user?.name)}
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-white text-sm font-semibold max-w-[150px] truncate">{user?.name}</span>
                <span className="text-white/60 text-[11px] uppercase tracking-wide">{roleLabel}</span>
              </div>
            </div>

            {isAdmin && (
              <HeaderAction as={Link} to="/supervisione" icon={Eye} label="Supervisione" testId="supervision-link" />
            )}
            {isAdmin && (
              <HeaderAction as={Link} to="/admin" icon={UserCog} label="Admin" testId="admin-link" />
            )}
            <HeaderAction icon={Lock} label="Password" testId="change-password-btn" onClick={() => setShowChangePassword(true)} />
            <HeaderAction icon={LogOut} label="Esci" testId="logout-btn" tone="danger" onClick={() => setShowLogoutConfirm(true)} />
          </div>
        </div>
      </header>

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}

      <AnimatePresence>
        {showLogoutConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowLogoutConfirm(false)} data-testid="logout-confirm-modal">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-lg font-medium text-[#1B291B]">Confermi l'uscita?</h3>
              </div>
              <p className="text-sm text-[#5C6E5C] mb-5">Verrai disconnesso dal tuo account e dovrai effettuare di nuovo l'accesso.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowLogoutConfirm(false)} className="el-btn-secondary" data-testid="logout-cancel-btn">Annulla</button>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium text-sm py-2.5 px-5 transition-colors"
                  data-testid="logout-confirm-btn"
                >
                  <LogOut className="w-4 h-4" /> Esci
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
